import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate, Link } from 'react-router-dom';
import Sidebar from '@/components/shared/Sidebar';
import { Home, Users, Briefcase, Wallet, Settings, AlertCircle, Building2, Upload } from 'lucide-react';
import { staffingAPI } from '@/services/staffingAPI';
import { walletAPI } from '@/services/api';

const navItems = [
  { to: '/staffing', icon: Home, label: 'Dashboard' },
  { to: '/staffing/candidates', icon: Users, label: 'Candidates' },
  { to: '/staffing/team', icon: Briefcase, label: 'Team' },
  { to: '/staffing/wallet', icon: Wallet, label: 'Wallet' },
  { to: '/staffing/settings', icon: Settings, label: 'Settings' },
];

function PendingApprovalState({ org }) {
  return (
    <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-8 text-center" data-testid="staffing-pending-approval">
      <AlertCircle className="w-10 h-10 mx-auto text-amber-700 dark:text-amber-300 mb-3" />
      <h2 className="text-slate-900 dark:text-white text-xl font-medium mb-1">Awaiting admin approval</h2>
      <p className="text-slate-500 dark:text-zinc-400 text-sm max-w-md mx-auto">Your staffing org <span className="text-slate-900 dark:text-white">{org?.name}</span> (@{org?.domain}) has been submitted to Numuv admin. You&apos;ll be notified once approved and can then onboard candidates.</p>
    </div>
  );
}

function StaffingHome() {
  const [ctx, setCtx] = useState(null);
  const [balance, setBalance] = useState(null);
  const [counts, setCounts] = useState({ candidates: 0 });
  useEffect(() => {
    (async () => {
      try { setCtx(await staffingAPI.me()); } catch { /* ignore */ }
      try { const w = await walletAPI.me(); setBalance(w?.balance ?? 0); } catch { /* ignore */ }
      try { const c = await staffingAPI.listCandidates({ limit: 1 }); setCounts({ candidates: c.total || 0 }); } catch { /* ignore */ }
    })();
  }, []);
  const approved = ctx?.organization?.org_approved;
  return (
    <div data-testid="staffing-home">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Staffing Dashboard</h1>
        <p className="text-slate-500 dark:text-zinc-500 text-sm flex items-center gap-2 mt-1">
          <Building2 className="w-4 h-4" /> {ctx?.organization?.name} <span className="text-slate-600 dark:text-zinc-700">·</span> @{ctx?.organization?.domain}
        </p>
      </div>
      {!approved && ctx?.organization && <PendingApprovalState org={ctx.organization} />}
      {approved && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl p-5">
            <div className="text-slate-500 dark:text-zinc-500 text-xs uppercase tracking-wider mb-1">Candidates</div>
            <div className="text-3xl font-semibold text-slate-900 dark:text-white" data-testid="stat-candidates">{counts.candidates}</div>
            <Link to="/staffing/candidates" className="text-emerald-600 dark:text-emerald-400 text-xs hover:underline mt-3 inline-block">Manage →</Link>
          </div>
          <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl p-5">
            <div className="text-slate-500 dark:text-zinc-500 text-xs uppercase tracking-wider mb-1">Wallet Balance</div>
            <div className={`text-3xl font-semibold ${balance >= 0 ? 'text-slate-900 dark:text-white' : 'text-red-600 dark:text-red-400'}`} data-testid="stat-balance">{balance ?? '—'} <span className="text-sm text-slate-500 dark:text-zinc-500">MP</span></div>
            <Link to="/staffing/wallet" className="text-emerald-600 dark:text-emerald-400 text-xs hover:underline mt-3 inline-block">View transactions →</Link>
          </div>
          <div className="bg-gradient-to-br from-emerald-500/10 via-white dark:via-[#111114] to-white dark:to-[#111114] border border-emerald-500/20 rounded-xl p-5">
            <Upload className="w-5 h-5 text-emerald-700 dark:text-emerald-300 mb-2" />
            <p className="text-slate-900 dark:text-white text-sm font-medium">Bulk-upload resumes</p>
            <p className="text-slate-500 dark:text-zinc-500 text-xs mt-1">Onboard up to 10 candidates at a time. Jay parses and creates profiles automatically.</p>
            <Link to="/staffing/candidates" className="inline-block mt-3 px-3 py-1.5 bg-emerald-500 text-black rounded-full text-xs font-medium hover:bg-emerald-400">Open candidate manager</Link>
          </div>
        </div>
      )}
    </div>
  );
}

import StaffingCandidates from './StaffingCandidates';
import StaffingCandidateDetail from './StaffingCandidateDetail';
import StaffingTeam from './StaffingTeam';
import StaffingWallet from './StaffingWallet';
import StaffingSettings from './StaffingSettings';
import ContactUs from '@/pages/ContactUs';

export default function StaffingDashboard() {
  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-[#0a0a0b]" data-testid="staffing-dashboard">
      <Sidebar items={navItems} title="Staffing" />
      <main className="flex-1 overflow-auto pt-14 lg:pt-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <Routes>
            <Route index element={<StaffingHome />} />
            <Route path="candidates" element={<StaffingCandidates />} />
            <Route path="candidates/:id" element={<StaffingCandidateDetail />} />
            <Route path="team" element={<StaffingTeam />} />
            <Route path="wallet" element={<StaffingWallet />} />
            <Route path="settings" element={<StaffingSettings />} />
            <Route path="contact" element={<ContactUs />} />
            <Route path="contact/ticket/:ticketId" element={<ContactUs />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
